/*
 * Copyright 2022 Benjamin Menant.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as React from 'react';
import {
    Card,
    CardHeader,
    CardBody,
    Text,
    TextContent,
    Title,
    Level,
    LevelItem,
    DataList,
    DataListItem,
    DataListItemRow,
    DataListItemCells,
    DataListCell,
    DataListAction,
    Spinner, AlertVariant,
} from '@patternfly/react-core';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { DownloadIcon } from '@patternfly/react-icons';
import { IAMTaxReceiptJsonSchema } from '../../donor-service/types/IAMTaxReceiptJsonSchema';
import {getTaxReceiptLink} from "../../api/methods.ts";
import {useEnvironmentWithDonorService} from "../../context.tsx";
import {useState} from "react";
import {useAccountAlerts} from "@keycloak/keycloak-account-ui";

type TaxReceiptProps = {
    year: number;
    receipts: IAMTaxReceiptJsonSchema[],
}

function ReceiptList ({ t, receipts, year }: { t: TFunction } & TaxReceiptProps) {
    const context = useEnvironmentWithDonorService();
    const { addAlert } = useAccountAlerts();

    const [loadingReceipt, setLoadingReceipt] = useState(false);

    const row: React.ReactNode[] = receipts.map(
        (receipt) => {
            const { id, refNumber, href } = receipt;
            const niceRef = (href ? refNumber : id.toString())
                .padStart(8, '0')
                .match(/\w{1,4}/g)!
                .join('\u202f');
            const safeRef = refNumber.replace(/[^a-z0-9]/gi, '');
            const safeId = `receipt-${year}-${safeRef}`;
            const safeIdAction = `${safeId}-download`;
            const target = 'ape-tax-receipt';
            const [remoteHref, setRemoteHref] = useState<string>();

            const handleClick = async (e: React.SyntheticEvent): Promise<void> => {
                e.preventDefault();
                try {
                    setLoadingReceipt(true);
                    const url = await getTaxReceiptLink(context, receipt);
                    setRemoteHref(url.href);
                    setLoadingReceipt(false);
                    window.open(url, target, 'noopener,noreferrer')?.focus();
                }
                catch (err) {
                    addAlert(t('ErrorFetchingTaxReceipt'), AlertVariant.danger)
                    setLoadingReceipt(false);
                    console.error(err);
                }
            }

            const DownloadLink = ({ children }: React.PropsWithChildren) =>
                <Text
                    onClick={href || remoteHref || loadingReceipt ? undefined : handleClick}
                    style={{cursor: loadingReceipt ? 'progress' : 'pointer' }}
                    component="a"
                    href={href || remoteHref || '#'}
                    download={`rf-ape-${year}-${safeRef}.pdf`}
                    target={target}
                    rel="noreferrer noopener"
                >
                    {children}
                </Text>;

            return (
                <DataListItem key={safeId} aria-labelledby={safeId}>
                    <DataListItemRow>
                        <DataListItemCells dataListCells={[
                            <DataListCell key="refNumber" style={{flex: "auto"}}>
                                <Title size="md" headingLevel="h4" id={safeId}>
                                    <DownloadLink>
                                        {t("receiptNumber", { ref: niceRef })}
                                    </DownloadLink>
                                    {loadingReceipt && <Spinner style={{marginLeft:'1em'}} size="md" aria-label={t("loading")} />}
                                </Title>
                            </DataListCell>,
                        ]} />
                        <DataListAction
                            id={safeIdAction}
                            style={{flexShrink: 0}}
                            aria-labelledby={`${safeIdAction} ${safeId}`}
                            aria-label={t("doDownload")}
                        >
                            <DownloadLink>
                                {
                                    loadingReceipt
                                        ? <Spinner style={{margin:'0 3em'}} size="md" aria-label={t("loading")} />
                                        : <strong>{t("doDownload")}&nbsp;<DownloadIcon /></strong>
                                }
                            </DownloadLink>
                        </DataListAction>
                    </DataListItemRow>
                </DataListItem>
            );
        },
    );
    return (
        <DataList aria-label={
            t(
                receipts.length > 1 ? 'nbOfReceipts' : 'oneReceipt',
                { year: String(year), qty: String(receipts.length) },
            )
        }>
            {row}
        </DataList>
    );
}

export function TaxReceipt ({
    receipts,
    year,
}: TaxReceiptProps) {
    const { t } = useTranslation();

    return (
        <Card className="ape-c-card">
            <CardHeader>
                <Level hasGutter>
                    <LevelItem><Title size="4xl" headingLevel="h2">🧾</Title></LevelItem>
                    <LevelItem>
                        <Title size="2xl" headingLevel="h2">
                            <strong><time>{year}</time></strong>
                        </Title>
                    </LevelItem>
                </Level>
            </CardHeader>
            <CardBody>
                <TextContent>
                    <Title size="md" headingLevel="h3">{
                        t(
                            receipts.length > 1 ? 'nbOfReceipts' : 'oneReceipt',
                            { year: String(year), qty: String(receipts.length) },
                        )
                    }</Title>
                </TextContent>
                <ReceiptList t={t} receipts={receipts} year={year} />
            </CardBody>
        </Card>
    );
}
